import queryString from 'query-string';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	ErrorResponse,
	ParsedQueryParameters,
	BoardLocationResult,
	BoardQueryParameters,
	CachedFilters,
} from './types.tsx';

const SOFTWARE_BOARD_REGEX = /\/jira\/software\/c\/?projects\/\w+\/boards\/(\d+)\/?(\?.*)?$/;
const USER_LOCATED_BOARD_REGEX = /\/jira\/people\/[^/]+\/boards\/(\d+)\/?(\?.*)?$/;

// people/project match needs to consider `...:...` ids
const SOFTWARE_BACKLOG_REGEX =
	/\/jira\/(?:software\/c\/)?(?:projects|people)\/[^/]+\/boards\/(\d+)\/backlog\/?(\?.*)?$/;

const parsePath = (pathname: string, pattern: RegExp): { boardId: number } | null => {
	const match = pattern.exec(pathname);
	if (!match) {
		return null;
	}
	const boardId = match[1];
	return { boardId: Number(boardId) };
};

// Safety check to guarantee that whatever we add here is an array
export const convertQuickFiltersToString = (
	queryParameters: BoardQueryParameters | undefined,
): string | undefined => {
	// Terminate early for the null/undefined cases
	if (!queryParameters?.quickFilter) {
		return undefined; // This is safe to return as it means the query string will just be excluded
	}

	if (!Array.isArray(queryParameters.quickFilter)) {
		// If for whatever reason an object gets in here
		if (typeof queryParameters.quickFilter === 'object') {
			// Fail gracefully
			return undefined;
		}

		// Ensure that the value is a number
		if (Number.isNaN(Number(queryParameters.quickFilter))) return undefined;

		// this isn't an array. It's probably a string or number which is fine to return as is.
		return `${queryParameters.quickFilter}`;
	}

	// Array passed in is the common case
	// If it is empty, don't include it at all.
	if (queryParameters.quickFilter.length === 0) {
		return undefined;
	}

	return queryParameters.quickFilter.filter((val) => Number.isInteger(Number(val))).join(',');
};

export const getCachedQuickFilters = (feature: 'board' | 'backlog', boardId: string | number) => {
	const localStorageKey = `__storejs_software_${feature}-filters.${boardId}`;
	const updatedStorageKey = `software.${feature}-filters.${boardId}`;

	let cachedFilters;
	const storage = localStorage;
	if (fg('jfp-vulcan-browser-storage-migration')) {
		// if new keys does not exist, falls back to older keys
		cachedFilters = storage.getItem(updatedStorageKey) || localStorage.getItem(localStorageKey);
	} else {
		cachedFilters = storage.getItem(localStorageKey);
	}

	if (!cachedFilters) {
		return undefined;
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const { CUSTOM_FILTER: cachedQuickFilters } = JSON.parse(cachedFilters) as CachedFilters;
	return cachedQuickFilters && cachedQuickFilters.length > 0
		? cachedQuickFilters.join(',')
		: undefined;
};

export const parseBoardLocation = ({ pathname, search }: Location): BoardLocationResult | null => {
	const parsedPath =
		parsePath(pathname, SOFTWARE_BOARD_REGEX) ?? parsePath(pathname, USER_LOCATED_BOARD_REGEX);
	if (!parsedPath) {
		return null;
	}

	const queryStringParameters = queryString.parse(search) ?? {};

	// Early termination if the quick filter isnt a part of the query string
	if (!queryStringParameters.quickFilter) {
		return {
			...parsedPath,
		};
	}

	// Guarantee that the quick filter will be returned as an array. This helps with typing further upstream
	const quickFilterArray = Array.isArray(queryStringParameters.quickFilter)
		? queryStringParameters.quickFilter
		: [queryStringParameters.quickFilter];

	return {
		...parsedPath,
		queryParameters: {
			quickFilter: quickFilterArray,
		},
	};
};

export const parseBacklogLocation = ({
	pathname,
	search,
}: Location): {
	boardId: number;
	queryParameters: ParsedQueryParameters;
} | null => {
	const parsedPath = parsePath(pathname, SOFTWARE_BACKLOG_REGEX);
	if (!parsedPath) {
		return null;
	}

	return {
		...parsedPath,
		queryParameters: queryString.parse(search) ?? {},
	};
};

const isErrorMessagesResponse = (json: unknown): json is ErrorResponse =>
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	(json as ErrorResponse)?.errorMessages instanceof Array;

export const extractErrorMessage = (json: unknown): string | undefined => {
	if (isErrorMessagesResponse(json)) {
		const { errorMessages } = json;
		return errorMessages.length ? errorMessages.join(', ') : undefined;
	}
	return undefined;
};

/* Copied from jira/src/packages/software/view-settings/src/services/local-storage/index.tsx
 to reduce bundle size by using native localStorage */

const VIEW_SETTINGS = 'view-settings';
export const VIEW_SETTINGS_LOCAL_STORAGE = 'jira-software-view-settings';
export const View = {
	BACKLOG: 'jsw-backlog',
	BOARD: 'jsw-board',
	INCREMENT_PLANNING_BOARD: 'increment-planning-board',
	JSM_BOARD: 'jsm-board',
};

export const getPrefix = (view?: (typeof View)[keyof typeof View]): string => {
	if (view && view === View.INCREMENT_PLANNING_BOARD) {
		return `${VIEW_SETTINGS}-${View.INCREMENT_PLANNING_BOARD}`;
	}
	if (fg('jira_view_settings_additional_fields')) {
		switch (view) {
			case View.INCREMENT_PLANNING_BOARD:
				return `${VIEW_SETTINGS}-${View.INCREMENT_PLANNING_BOARD}`;
			case View.BACKLOG:
				return `${VIEW_SETTINGS}-${View.BACKLOG}`;
			case View.BOARD:
				return `${VIEW_SETTINGS}-${View.BOARD}`;
			case View.JSM_BOARD:
				return `${VIEW_SETTINGS}-${View.JSM_BOARD}`;
			default:
				return VIEW_SETTINGS;
		}
	}
	return VIEW_SETTINGS;
};

export type ViewSettingsState = {
	view: (typeof View)[keyof typeof View] | null;
	isFullscreen: boolean;
	showEpicPanel: boolean;
	showVersionPanel: boolean;
	showQuickFilters: boolean;
	showCompactMode: boolean;
	showEpics: boolean;
	showStatuses: boolean;
	showVersions: boolean;
	showUnscheduledColumn: boolean;
	showIssueLinksStats: boolean;
	showIssueType: boolean;
	showIssueKey: boolean;
	showPriority: boolean;
	showDevelopment: boolean;
	showAssignee: boolean;
	showEstimate: boolean;
	showDaysInColumn: boolean;
	showDueDate: boolean;
	showLabels: boolean;
	showCardCovers: boolean;
	showOfftrackDependencyLines: boolean;
	issueIdsToShowDependencies: (string | number)[];
	cardExtraFields: { [key: string]: boolean };
};

// The input and return types of this function should align with GetViewSettingsProps and State
// from @atlassian/jira-software-view-settings. They have been duplicated here because creating
// the relationship between these packages causes a HUGE increase in relationship complexity in Jira.
// If @atlassian/jira-software-view-settings gets decomposed, this relationship could be re-established.
export const getViewSettings = ({
	boardId,
	view,
}: {
	boardId: number;
	view?: string;
}): ViewSettingsState | null => {
	/**
	 * This usage is excluded via package exclusion in jira/ratcheting-rules.tsx
	 * Because it is only a READ Operation, and reads are not blocked in compliant APIs.
	 * This read will be tracked via a compliant API read in following code
	 * - Initializing view-settings panel on board: jira/src/packages/software/view-settings/src/ui/index.tsx
	 * - rendering cards on the board (Implementation In Progress https://hello.jira.atlassian.cloud/browse/PNDR-1926)
	 * More context in this discussion: https://atlassian.slack.com/archives/C02785CLSTW/p1722822320190909
	 */

	const localStorageKey = `__storejs_${VIEW_SETTINGS_LOCAL_STORAGE}_${getPrefix(view)}-${boardId}`;
	const updatedStorageKey = `${VIEW_SETTINGS_LOCAL_STORAGE}.${getPrefix(view)}-${boardId}`;
	const legacyStorageKey = `__storejs_${VIEW_SETTINGS_LOCAL_STORAGE}_${VIEW_SETTINGS}-${boardId}`;
	const updatedLegacyStorageKey = `${VIEW_SETTINGS_LOCAL_STORAGE}.${VIEW_SETTINGS}-${boardId}`;
	const storage = localStorage;
	let viewSettingStorageValue;
	if (fg('jfp-vulcan-browser-storage-migration')) {
		viewSettingStorageValue =
			storage.getItem(updatedStorageKey) ||
			storage.getItem(updatedLegacyStorageKey) ||
			// if new keys does not exist, falls back to older keys
			storage.getItem(localStorageKey) ||
			storage.getItem(legacyStorageKey);
	} else {
		viewSettingStorageValue =
			storage.getItem(localStorageKey) ||
			// return legacy view settings if prefixed storage doesn't exist
			storage.getItem(legacyStorageKey);
	}
	return JSON.parse(viewSettingStorageValue ?? 'null');
};
